import React, { useEffect, useState } from 'react';
import { PDFDownloadLink } from '@react-pdf/renderer';
import { supabase } from '../../lib/supabase';
import StatusBadge from '../../components/StatusBadge';
import FactuurDocument from '../../components/pdf/FactuurDocument';
import { getFactuurPdfLabels, getFactuurStatusLabels, pdfFmtDate } from '../../components/pdf/pdfFactuurLabels';
import { t } from '../../i18n/nl.js';
import { useAuth } from '../../contexts/AuthContext';
import { euro, fmtDate } from '../../utils/formatUtils.js';

export default function ClientFacturenPage() {
  const { user } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [invoiceLines, setInvoiceLines] = useState({});
  const [company, setCompany] = useState({});
  const [clientProfile, setClientProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    async function load() {
      setLoading(true);
      setError('');
      try {
        const [invoicesRes, settingsRes, profileRes] = await Promise.all([
          supabase
            .from('invoices')
            .select('id, invoice_number, subtotal, btw_amount, total_amount, status, created_at, period_start, period_end, due_date, invoice_reminders(type, sent_at, new_due_date)')
            .eq('client_id', user.id)
            .order('created_at', { ascending: false }),
          supabase
            .from('settings')
            .select('key, value'),
          supabase
            .from('profiles')
            .select('id, full_name, email, customer_number, street, house_number, postal_code, city')
            .eq('id', user.id)
            .maybeSingle(),
        ]);

        if (invoicesRes.error) throw invoicesRes.error;
        setInvoices(invoicesRes.data || []);
        setClientProfile(profileRes.data || null);

        const map = {};
        (settingsRes.data || []).forEach(({ key, value }) => { map[key] = value; });
        setCompany(map);
      } catch (err) {
        setError(t('mijnFacturen.foutLaden') + err.message);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [user?.id]);

  async function loadLinesForInvoice(invoiceId) {
    if (invoiceLines[invoiceId]) return invoiceLines[invoiceId];
    const { data } = await supabase
      .from('invoice_lines')
      .select('id, description, quantity, unit_price, btw_percentage, amount')
      .eq('invoice_id', invoiceId)
      .order('position');
    const lines = data || [];
    setInvoiceLines(prev => ({ ...prev, [invoiceId]: lines }));
    return lines;
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">{t('mijnFacturen.title')}</h1>
          <p className="page-subtitle">{invoices.length} {t('mijnFacturen.aantalFacturen')}</p>
        </div>
      </div>

      {error && <div className="error-msg" style={{ marginBottom: 20 }}>{error}</div>}

      {loading ? (
        <div className="loading-center"><div className="spinner" /></div>
      ) : (
        <div className="card" style={{ padding: 0 }}>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>{t('mijnFacturen.colNummer')}</th>
                  <th>{t('facturen.colPeriode')}</th>
                  <th>{t('mijnFacturen.colDatum')}</th>
                  <th>{t('mijnFacturen.colTotaal')}</th>
                  <th>{t('mijnFacturen.colStatus')}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {invoices.length === 0 ? (
                  <tr>
                    <td colSpan={6}>
                      <div className="empty-state">
                        <div style={{ fontSize: 32 }}>🧾</div>
                        <p>{t('mijnFacturen.geenFacturen')}</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  invoices.map(inv => {
                    const sortedReminders = inv.invoice_reminders?.length
                      ? [...inv.invoice_reminders].sort((a, b) => new Date(a.sent_at) - new Date(b.sent_at))
                      : [];
                    return (
                      <React.Fragment key={inv.id}>
                        <tr>
                          <td className="font-medium">{inv.invoice_number}</td>
                          <td className="text-muted">
                            {fmtDate(inv.period_start)} – {fmtDate(inv.period_end)}
                          </td>
                          <td className="text-muted">{fmtDate(inv.created_at)}</td>
                          <td className="font-semibold">{euro(inv.total_amount)}</td>
                          <td><StatusBadge status={inv.status} /></td>
                          <td>
                            <ClientPdfButton
                              inv={{
                                ...inv,
                                client: {
                                  id: user?.id,
                                  email: clientProfile?.email || user?.email,
                                  full_name: clientProfile?.full_name,
                                  customer_number: clientProfile?.customer_number,
                                  street: clientProfile?.street,
                                  house_number: clientProfile?.house_number,
                                  postal_code: clientProfile?.postal_code,
                                  city: clientProfile?.city,
                                },
                              }}
                              onLoadLines={loadLinesForInvoice}
                              lines={invoiceLines[inv.id]}
                              company={company}
                            />
                          </td>
                        </tr>
                        {sortedReminders.length > 0 && (
                          <tr style={{ background: 'var(--color-bg)' }}>
                            <td colSpan={6} style={{ paddingTop: 4, paddingBottom: 12, paddingLeft: 24 }}>
                              <div style={{ borderLeft: '3px solid var(--color-border)', paddingLeft: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
                                {sortedReminders.map((r, i) => (
                                  <div key={i} style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
                                    <span style={{ marginRight: 6 }}>📋</span>
                                    <span style={{ marginRight: 6 }}>{fmtDate(r.sent_at)} —</span>
                                    <span style={{ fontWeight: 600, color: 'var(--color-text)' }}>
                                      {r.type === 'reminder'
                                        ? t('mijnFacturen.histTypeReminder')
                                        : r.type === 'final_notice'
                                          ? t('mijnFacturen.histTypeFinalNotice')
                                          : t('mijnFacturen.histTypeCollections')}
                                    </span>
                                    {r.new_due_date && (
                                      <div style={{ paddingLeft: 22 }}>
                                        {t('mijnFacturen.histNieuweBetaaldatum')} {fmtDate(r.new_due_date)}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function ClientPdfButton({ inv, onLoadLines, lines, company }) {
  const [ready, setReady] = useState(false);
  const [localLines, setLocalLines] = useState(lines || []);

  async function handleClick() {
    if (!lines) {
      const loaded = await onLoadLines(inv.id);
      setLocalLines(loaded);
    }
    setReady(true);
  }

  if (!ready) {
    return (
      <button className="btn btn-ghost btn-sm" onClick={handleClick}>
        {t('mijnFacturen.downloadFactuur')}
      </button>
    );
  }

  return (
    <PDFDownloadLink
      document={<FactuurDocument invoice={inv} lines={localLines} company={company} labels={getFactuurPdfLabels()} statusLabels={getFactuurStatusLabels()} fmtDate={pdfFmtDate} />}
      fileName={`${inv.invoice_number}.pdf`}
      style={{ textDecoration: 'none' }}
    >
      {({ loading }) => (
        <button className="btn btn-ghost btn-sm" disabled={loading}>
          {loading ? t('mijnFacturen.genererenPdf') : t('common.pdf')}
        </button>
      )}
    </PDFDownloadLink>
  );
}
